import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './HorizontalScrollCarousel.css';
import { images } from '../../shared/assets';
import { initializeDragScroll } from '../../../utils/interactiveFeatures';
import { HandDrawnButton } from '../../shared/HandDrawnButton/HandDrawnButton';
import { useTranslation } from '../../../i18n';

interface IntroCopyProps {
  onAboutUsClick: () => void;
}

const IntroCopy: React.FC<IntroCopyProps> = ({ onAboutUsClick }) => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__intro-copy">
      <div className="horizontal-scroll-carousel__intro-bg" />
      <div className="horizontal-scroll-carousel__text-content">
        <div className="horizontal-scroll-carousel__title">
          <p>Where Time and Space are Yours to Own</p>
        </div>
        <div className="horizontal-scroll-carousel__description-container">
          <div className="horizontal-scroll-carousel__description">
            <p>At Awasi, our lodges are designed to feel like the home of an old friend, guiding you through stunning landscapes, native flavors and hidden gems. It's a fusion of friendship, admiration for the place, and personal hospitality that creates a genuine connection with each guest.​</p>
          </div>
          <HandDrawnButton 
            text="About us"
            onClick={onAboutUsClick}
            variant="brand"
            color="awasi"
            animation="stroke-to-fill"
            className="horizontal-scroll-carousel__button"
            ariaLabel={t('accessibility.buttons.learnMore') + ' - About us'}
          />
        </div>
      </div>
    </div>
  );
};

const VerticalImagePanel: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__vertical-panel">
      <img
        src={images.brandHomePage.carousel.rightBlock}
        alt={t('accessibility.images.landscape')}
        className="horizontal-scroll-carousel__vertical-image"
      />
    </div>
  );
};

interface DiptychProps {
  onAboutUsClick: () => void;
}

const Diptych: React.FC<DiptychProps> = ({ onAboutUsClick }) => {
  return (
    <div className="horizontal-scroll-carousel__diptych">
      <IntroCopy onAboutUsClick={onAboutUsClick} />
      <VerticalImagePanel />
    </div>
  );
};

const ImageCollage: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__collage">
      <div className="horizontal-scroll-carousel__collage-image1">
        <img
          src={images.brandHomePage.carousel.image1}
          alt={t('accessibility.images.propertyExterior')}
          className="horizontal-scroll-carousel__collage-img"
        />
      </div>
      <div className="horizontal-scroll-carousel__collage-image2">
        <img
          src={images.brandHomePage.carousel.image2}
          alt={t('accessibility.images.experience')}
          className="horizontal-scroll-carousel__collage-img"
        />
      </div>
      <div className="horizontal-scroll-carousel__collage-image3">
        <img
          src={images.brandHomePage.carousel.image3}
          alt={t('accessibility.images.landscape')}
          className="horizontal-scroll-carousel__collage-img"
        />
      </div>
    </div>
  );
};

const FullScreenPanel: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__fullscreen">
      <img
        src={images.brandHomePage.carousel.fullImage}
        alt={t('accessibility.images.heroImage')}
        className="horizontal-scroll-carousel__fullscreen-image"
      />
    </div>
  );
};

const Outro: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__outro">
      <div className="horizontal-scroll-carousel__outro-text">
        <p>
          In the remote corners of the South American continent — where guardian mountains rise, eternal ice lingers, stars burn brighter, the jungle whispers with wonder and clear crystal beaches glisten in the sun  — Awasi Lodges was born to curate your journey.​
        </p>
      </div>
      <div className="horizontal-scroll-carousel__outro-image">
        <img
          src={images.brandHomePage.carousel.smallImage}
          alt={t('accessibility.images.landscape')}
          className="horizontal-scroll-carousel__outro-img"
        />
      </div>
    </div>
  );
};

export const HorizontalScrollCarousel: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const handleAboutUsClick = () => {
    navigate('/our-story');
  };

  useEffect(() => {
    // Initialize drag scroll with the correct container ID
    const cleanup = initializeDragScroll('draggable-carousel');
    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, []);

  return (
    <section 
      className="horizontal-scroll-carousel" 
      data-scroll-section
      aria-label={t('accessibility.media.carousel')}
    >
      <div 
        ref={scrollRef}
        className="horizontal-scroll-carousel__scroll-container"
        id="draggable-carousel"
        role="region"
        aria-label={t('accessibility.media.carousel')}
      >
        {/* First frame - Diptych */}
        <Diptych onAboutUsClick={handleAboutUsClick} />
        
        {/* Second frame - Image Collage */}
        <ImageCollage />
        
        {/* Third frame - Full Screen Image */}
        <FullScreenPanel />
        
        {/* Fourth frame - Outro */}
        <Outro />
      </div>
    </section>
  );
};